import { ErrorNotice } from '../../components/ErrorNotice.js';
import { useEffect, useState } from 'react';
import { listSubjects, type Subject } from '../../lib/api/endpoints.js';
import { subjectDisplayName } from '../../lib/subjects.js';
import {
  computeCreditRules,
  computeStamps,
  nextVolumeMilestone,
  totalCredits,
  useObservationDigest,
} from '../../lib/standing.js';

/**
 * Field record — what a contributor's listening adds up to.
 *
 * Lives on its own route, deliberately not folded into the timeline. The
 * timeline answers "what do I know about this animal now that I didn't know
 * last week" — this page answers "what have I built". Mixing the two would
 * make an animal's own page about the contributor, which is backwards for a
 * product whose entire thesis is that the animal is the subject.
 *
 * Interim and honest about it: three axes exist in the design (volume,
 * craft, rarity), but only volume and one craft signal (full A/B/C context)
 * can be computed from what the client already has — see lib/standing.ts.
 * Clean-audio, lossless and rarity all need either fields the API doesn't
 * return yet or corpus-wide data no client has, so those rows are hidden
 * rather than shown-and-permanently-unearned.
 */
export function StandingPage() {
  const [subjects, setSubjects] = useState<Subject[] | null>(null);
  const { digest, error: digestError, refresh } = useObservationDigest();
  const [error, setError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setError(null);
    listSubjects().then(setSubjects).catch(setError);
  }, [attempt]);

  const failure = error ?? digestError;
  if (failure) {
    return (
      <ErrorNotice
        error={failure}
        title="We couldn't load your field record."
        onRetry={() => {
          setAttempt((n) => n + 1);
          refresh();
        }}
      />
    );
  }
  if (digest === null || subjects === null) return <p className="meta">Loading…</p>;

  // The one animal this page's per-subject line refers to: whichever was
  // recorded most recently, the same "usual animal" the record flow and its
  // subject picker default to.
  const highlighted = [...subjects].sort((a, b) => {
    const aAt = digest.lastObservedAtBySubject[a.id];
    const bAt = digest.lastObservedAtBySubject[b.id];
    if (aAt && bAt) return bAt.localeCompare(aAt);
    if (aAt) return -1;
    if (bAt) return 1;
    return 0;
  })[0];
  const highlightedName = highlighted ? subjectDisplayName(highlighted) : null;
  const highlightedCount = highlighted ? (digest.bySubject[highlighted.id] ?? 0) : 0;

  const milestone = nextVolumeMilestone(digest.total);
  const stamps = computeStamps(digest);
  const creditRules = computeCreditRules(digest);
  const credits = totalCredits(creditRules);
  const fullContextRow = creditRules.find((r) => r.key === 'full-context')!;
  const earnedCount = stamps.filter((s) => s.on).length;

  return (
    <div>
      <p className="eyebrow">Field record</p>
      <h1 className="page-title">What you've built so far</h1>
      <p className="page-intro">
        Every recording makes one animal better understood — and the corpus a little less thin. This
        page is the second half of that: what your listening adds up to.
      </p>

      <section className="standing">
        <p className="standing-kicker">Recordings</p>
        <div className="standing-count">
          <span className="standing-total">{digest.total}</span>
        </div>
        <p className="standing-sub">
          recordings in the corpus
          {highlightedName
            ? highlightedCount > 0
              ? ` · ${highlightedCount} of them from ${highlightedName}`
              : ` · None from ${highlightedName} yet`
            : ''}
        </p>

        {milestone.next !== null && (
          <>
            <div className="standing-next">
              <span>
                {milestone.next - digest.total} more to the {milestoneLabel(milestone.next)} stamp
              </span>
              <span className="standing-next-scale">
                {milestone.prev} → {milestone.next}
              </span>
            </div>
            <div className="standing-bar">
              <div
                className="standing-bar-fill"
                style={{ width: `${Math.round(milestone.fraction * 100)}%` }}
              />
            </div>
          </>
        )}

        <div className="credits">
          <span className="credits-total">{credits}</span>
          <span className="credits-label">Field credits</span>
        </div>
        <dl style={{ margin: 0 }}>
          {creditRules.map((rule) => (
            <div className="credit-rule-row" key={rule.key}>
              <dt>{rule.label}</dt>
              <dd className="credit-rule-count">× {rule.count}</dd>
              <dd className="credit-rule-rate">+{rule.rate}</dd>
            </div>
          ))}
        </dl>
        {digest.pending > 0 && (
          <p className="credits-caveat">
            {digest.pending === 1
              ? 'One of these is still waiting on review'
              : `${digest.pending} of these are still waiting on review`}
            . They count while they wait; anything a reviewer rejects stops counting, here and in
            the corpus.
          </p>
        )}
        <p className="credits-caveat">
          Credits accumulate from today and never expire. What they redeem for is still being decided
          with the first contributors — we would rather promise nothing than promise wrongly.
        </p>
      </section>

      <section className="standing" style={{ marginBottom: '1.5rem' }}>
        <p className="standing-kicker">Stamps — {earnedCount} of {stamps.length} collected</p>
        <div className="stamp-grid">
          {stamps.map((stamp) => (
            <div className={`stamp${stamp.on ? ' stamp--on' : ''}`} key={stamp.key}>
              <div className="stamp-ring">
                <span className="stamp-label">{stamp.label}</span>
              </div>
              <span className="stamp-note">{stamp.note}</span>
            </div>
          ))}
        </div>
        <p className="field-hint" style={{ marginTop: '1rem', textAlign: 'center' }}>
          Four for how much you record, one for how well so far — clean-audio and rare-call stamps
          need a server-side pass this app doesn't do yet. None of them expire, and none of them are
          ranked against anyone else.
        </p>
      </section>

      <section className="standing">
        <p className="standing-kicker">How usable your recordings are</p>
        <div className={`craft-row${fullContextRow.count >= 10 ? ' craft-row--done' : ''}`}>
          <div className="craft-row-head">
            <span>Full A / B / C context</span>
            <span className="craft-row-count">{Math.min(fullContextRow.count, 10)} / 10</span>
          </div>
          <div className="craft-row-bar">
            <span style={{ width: `${Math.max(4, Math.min(100, (fullContextRow.count / 10) * 100))}%` }} />
          </div>
          <span className="craft-row-note">
            Antecedent, signal and consequence all coded — the record a researcher can actually use.
          </span>
        </div>
      </section>

      <a
        className="btn"
        href="#/contribute"
        style={{ width: '100%', justifyContent: 'center', borderRadius: '999px', marginTop: '1.5rem' }}
      >
        Record something new
      </a>
    </div>
  );
}

function milestoneLabel(threshold: number): string {
  switch (threshold) {
    case 1:
      return 'first take';
    case 10:
      return 'ten in';
    case 25:
      return 'twenty-five';
    case 100:
      return 'hundred';
    default:
      return String(threshold);
  }
}
